import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FiUsers,
  FiPlus,
  FiMail,
  FiPhone,
  FiSearch,
  FiX,
  FiLock,
  FiUnlock,
  FiUpload,
  FiDownload,
  FiEdit2,
  FiGift,
  FiCheck,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiCheckCircle,
} from "react-icons/fi";
import {
  HiOutlineTableCells,
  HiOutlineDocumentText,
} from "react-icons/hi2";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CardSkeleton } from "@/components/common";

type Customer = {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  address: string | null;
  emailVerified: boolean;
  isLocked: boolean;
  lastLoginAt: string | null;
  birthday: string | null;
  loanSettlementDate: string | null;
  loanType: string | null;
  preferredName: string | null;
  customerNotes: string | null;
  createdAt: string;
  // CRM fields
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  title: string | null;
  gender: string | null;
  occupation: string | null;
  company: string | null;
  industry: string | null;
  abn: string | null;
  acn: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postCode: string | null;
  country: string | null;
  homePhone: string | null;
  workPhone: string | null;
  homeFax: string | null;
  workFax: string | null;
  spousePartner: string | null;
  selfEmployed: boolean;
  source: string | null;
  accountStatus: string | null;
  customerType: string | null;
  bestTimeToCall: string | null;
  emailStatus: string | null;
  neverEmail: boolean;
  dontSendMarketing: boolean;
  crmId: string | null;
  crmCreatedAt: string | null;
  productGroup: string | null;
  fixedPeriodMonths: number | null;
  driversLicence: string | null;
  jobTitle: string | null;
  contactMethod: string | null;
  campaign: string | null;
  channel: string | null;
  referenceId: string | null;
  loanCount: number;
};

const loanTypeLabels: Record<string, string> = {
  home_purchase: "Home Purchase",
  refinance: "Refinance",
  investment: "Investment",
  first_home: "First Home",
  construction: "Construction",
};

export function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [usageTypeFilter, setUsageTypeFilter] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lockingId, setLockingId] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerLoans, setCustomerLoans] = useState<Array<{ id: string; loanType: string | null; loanPurpose: string | null; loanCategory: string | null; loanAmount: number | null; lender: string | null; settlementDate: string | null }>>([]);
  const [importData, setImportData] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, imported: 0, updated: 0, skipped: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [importUsageType, setImportUsageType] = useState<string>("");
  const [importFormat, setImportFormat] = useState<"standard" | "trail_loans">("standard");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkUpdateProgress, setBulkUpdateProgress] = useState({ current: 0, total: 0 });
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ current: 0, total: 0, deleted: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const CUSTOMERS_PER_PAGE = 50;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state for new customer
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    birthday: "",
    loanSettlementDate: "",
    loanType: "",
    preferredName: "",
    customerNotes: "",
  });

  // Edit form state - includes all CRM fields
  const [editFormData, setEditFormData] = useState({
    name: "",
    phone: "",
    address: "",
    birthday: "",
    loanSettlementDate: "",
    loanType: "",
    preferredName: "",
    customerNotes: "",
    // CRM fields
    firstName: "",
    lastName: "",
    middleName: "",
    title: "",
    gender: "",
    occupation: "",
    company: "",
    industry: "",
    abn: "",
    acn: "",
    street: "",
    city: "",
    state: "",
    postCode: "",
    country: "",
    homePhone: "",
    workPhone: "",
    spousePartner: "",
    selfEmployed: false,
    source: "",
    accountStatus: "",
    customerType: "",
    bestTimeToCall: "",
    emailStatus: "",
    neverEmail: false,
    dontSendMarketing: false,
    productGroup: "",
    driversLicence: "",
    jobTitle: "",
    contactMethod: "",
    campaign: "",
    channel: "",
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/customers", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json() as Customer[];
        setCustomers(data);
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.name) {
      toast.error("Email and name are required");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/admin/customers", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json() as { error?: string };

      if (!response.ok) {
        throw new Error(data.error || "Failed to create customer");
      }

      toast.success("Customer created successfully");
      setIsDialogOpen(false);
      setFormData({
        email: "",
        name: "",
        phone: "",
        birthday: "",
        loanSettlementDate: "",
        loanType: "",
        preferredName: "",
        customerNotes: "",
      });
      fetchCustomers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create customer");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCustomer) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/admin/customers/${selectedCustomer.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editFormData),
      });

      const data = await response.json() as { error?: string };

      if (!response.ok) {
        throw new Error(data.error || "Failed to update customer");
      }

      toast.success("Customer updated");
      setIsEditDialogOpen(false);
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update customer");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleLock = async (customerId: string, currentlyLocked: boolean) => {
    setLockingId(customerId);

    try {
      const response = await fetch(`/api/admin/customers/${customerId}/lock`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isLocked: !currentlyLocked }),
      });

      const data = await response.json() as { error?: string };

      if (!response.ok) {
        throw new Error(data.error || "Failed to update customer");
      }

      toast.success(currentlyLocked ? "Customer unlocked" : "Customer locked");
      fetchCustomers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update customer");
    } finally {
      setLockingId(null);
    }
  };

  const handleDelete = async () => {
    if (!customerToDelete) return;

    setIsDeleting(true);

    try {
      const response = await fetch(`/api/admin/customers/${customerToDelete.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json() as { error?: string };

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete customer");
      }

      toast.success("Customer deleted");
      setIsDeleteDialogOpen(false);
      setCustomerToDelete(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete customer");
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (customer: Customer) => {
    setCustomerToDelete(customer);
    setIsDeleteDialogOpen(true);
  };

  const openEditDialog = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditFormData({
      name: customer.name || "",
      phone: customer.phone || "",
      address: customer.address || "",
      birthday: customer.birthday || "",
      loanSettlementDate: customer.loanSettlementDate
        ? new Date(customer.loanSettlementDate).toISOString().split("T")[0]
        : "",
      loanType: customer.loanType || "",
      preferredName: customer.preferredName || "",
      customerNotes: customer.customerNotes || "",
      // CRM fields
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      middleName: customer.middleName || "",
      title: customer.title || "",
      gender: customer.gender || "",
      occupation: customer.occupation || "",
      company: customer.company || "",
      industry: customer.industry || "",
      abn: customer.abn || "",
      acn: customer.acn || "",
      street: customer.street || "",
      city: customer.city || "",
      state: customer.state || "",
      postCode: customer.postCode || "",
      country: customer.country || "",
      homePhone: customer.homePhone || "",
      workPhone: customer.workPhone || "",
      spousePartner: customer.spousePartner || "",
      selfEmployed: customer.selfEmployed || false,
      source: customer.source || "",
      accountStatus: customer.accountStatus || "",
      customerType: customer.customerType || "",
      bestTimeToCall: customer.bestTimeToCall || "",
      emailStatus: customer.emailStatus || "",
      neverEmail: customer.neverEmail || false,
      dontSendMarketing: customer.dontSendMarketing || false,
      productGroup: customer.productGroup || "",
      driversLicence: customer.driversLicence || "",
      jobTitle: customer.jobTitle || "",
      contactMethod: customer.contactMethod || "",
      campaign: customer.campaign || "",
      channel: customer.channel || "",
    });
    setIsEditDialogOpen(true);

    // Fetch customer loans
    fetch(`/api/admin/customers/${customer.id}/loans`, { credentials: "include" })
      .then(res => res.json() as Promise<{ data?: Array<{ id: string; loanType: string | null; loanPurpose: string | null; loanCategory: string | null; loanAmount: number | null; lender: string | null; settlementDate: string | null }> }>)
      .then((data) => {
        if (data.data) setCustomerLoans(data.data);
      })
      .catch(() => setCustomerLoans([]));
  };

  const processFile = (file: File) => {
    // Try reading as UTF-16 first (CRM exports), then fallback to UTF-8
    const reader = new FileReader();
    reader.onload = (event) => {
      const arrayBuffer = event.target?.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);

      // Check for UTF-16 LE BOM (FF FE) or UTF-16 BE BOM (FE FF)
      const isUtf16LE = bytes[0] === 0xFF && bytes[1] === 0xFE;
      const isUtf16BE = bytes[0] === 0xFE && bytes[1] === 0xFF;

      let text: string;
      if (isUtf16LE) {
        text = new TextDecoder("utf-16le").decode(arrayBuffer);
      } else if (isUtf16BE) {
        text = new TextDecoder("utf-16be").decode(arrayBuffer);
      } else {
        text = new TextDecoder("utf-8").decode(arrayBuffer);
      }

      setImportData(text);
      toast.success(`File "${file.name}" loaded`);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];
    const validTypes = [".csv", ".tsv", ".txt"];
    const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

    if (!validTypes.includes(fileExt)) {
      toast.error("Please drop a CSV, TSV, or TXT file");
      return;
    }

    processFile(file);
  };

  const parseCSV = (csvText: string): Array<Record<string, string>> => {
    // Remove BOM and normalize line endings
    const cleanText = csvText
      .replace(/^\uFEFF/, "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n");

    const lines = cleanText.trim().split("\n");
    if (lines.length < 2) return [];

    const firstLine = lines[0];

    // Detect delimiter: tab or comma
    const tabCount = (firstLine.match(/\t/g) || []).length;
    const commaCount = (firstLine.match(/,/g) || []).length;
    const delimiter = tabCount > commaCount ? "\t" : ",";

    // Parse a CSV line respecting quoted fields
    const parseLine = (line: string): string[] => {
      const fields: string[] = [];
      let current = "";
      let inQuotes = false;

      for (let k = 0; k < line.length; k++) {
        const ch = line[k];

        if (inQuotes) {
          if (ch === '"') {
            // Check for escaped quote ("")
            if (k + 1 < line.length && line[k + 1] === '"') {
              current += '"';
              k++; // skip next quote
            } else {
              inQuotes = false;
            }
          } else {
            current += ch;
          }
        } else {
          if (ch === '"') {
            inQuotes = true;
          } else if (ch === delimiter) {
            fields.push(current);
            current = "";
          } else {
            current += ch;
          }
        }
      }
      fields.push(current);
      return fields;
    };

    // Parse headers
    const rawHeaders = parseLine(firstLine);
    const headers = rawHeaders.map((h, idx) => {
      const clean = h.trim().replace(/^["']|["']$/g, "");
      const normalized = clean.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      return normalized || `_empty_${idx}`;
    });

    console.log(`CSV: ${headers.length} columns, delimiter: ${delimiter === "\t" ? "TAB" : "COMMA"}`);

    const rows: Array<Record<string, string>> = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      const rawValues = parseLine(line);
      const row: Record<string, string> = {};
      const usedKeys = new Set<string>();

      for (let j = 0; j < headers.length; j++) {
        const header = headers[j];
        const rawValue = rawValues[j] || "";
        const value = rawValue.trim().replace(/^["']|["']$/g, "");

        if (value && !header.startsWith("_empty_")) {
          // Handle duplicate column names by appending index
          let key = header;
          if (usedKeys.has(header)) {
            key = `${header}_${j}`;
          }
          usedKeys.add(key);
          row[key] = value;
        }
      }

      rows.push(row);
    }

    // Log parsing results
    if (rows.length > 0) {
      const emailIdx = headers.indexOf("email");
      console.log(`Email column at index: ${emailIdx}`);
      console.log(`First row has ${Object.keys(rows[0]).length} fields`);
      console.log(`First row email:`, rows[0].email);

      if (!rows[0].email && emailIdx !== -1) {
        // Debug why email is missing
        const rawValues = parseLine(lines[1]);
        console.log(`Raw value at email index: "${rawValues[emailIdx]}"`);
      }
    }

    return rows;
  };

  // Helper to parse date in various formats (DD/MM/YYYY, YYYY-MM-DD, etc.)
  const parseDate = (dateStr: string): string => {
    if (!dateStr) return "";

    // Already in ISO format (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      return dateStr.split(" ")[0]; // Remove any time component
    }

    // Australian format DD/MM/YYYY
    const auMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (auMatch) {
      const [, day, month, year] = auMatch;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    // US format MM/DD/YYYY - assume if month > 12 it's actually AU format
    const usMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usMatch) {
      const [, first, second, year] = usMatch;
      // If first number > 12, it must be day (AU format)
      if (parseInt(first) > 12) {
        return `${year}-${second.padStart(2, "0")}-${first.padStart(2, "0")}`;
      }
      // Assume AU format by default for Australian CRM
      return `${year}-${second.padStart(2, "0")}-${first.padStart(2, "0")}`;
    }

    return dateStr;
  };

  // Build address from components
  const buildAddress = (row: Record<string, string>): string => {
    const parts = [
      row.street,
      row.city,
      row.state,
      row.post_code,
      row.country,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Detect if CSV is headerless by checking if the first field looks like an email
  const isHeaderlessCSV = (data: string): boolean => {
    const firstLine = data.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").split("\n").find(l => l.trim());
    if (!firstLine) return false;
    const firstField = firstLine.split(",")[0]?.replace(/"/g, "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(firstField);
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      toast.error("Please paste or upload CSV data");
      return;
    }

    // Auto-detect headerless CSV (first field is an email) and route to trail loans handler
    if (importFormat === "trail_loans" || isHeaderlessCSV(importData)) {
      return handleTrailLoansImport();
    }

    setIsImporting(true);

    try {
      const rows = parseCSV(importData);

      if (rows.length === 0) {
        toast.error("No valid data found in CSV");
        setIsImporting(false);
        return;
      }

      // Send all raw CSV data to the backend - it handles all field mapping
      // Just normalize dates to ISO format and pass everything through
      const customers = rows.map((row) => {
        const processed: Record<string, string> = {};

        for (const [key, value] of Object.entries(row)) {
          if (!value) continue;

          // Parse date fields to ISO format
          if (key.includes("date") || key.includes("birth") || key === "created" || key === "start_date" || key === "end_date") {
            processed[key] = parseDate(value);
          } else {
            processed[key] = value;
          }
        }

        // Build address from components if not present
        if (!processed.address) {
          const addr = buildAddress(row);
          if (addr) processed.address = addr;
        }

        // Ensure name is populated
        if (!processed.name && !processed.full_name) {
          const name = [row.first_name, row.last_name].filter(Boolean).join(" ");
          if (name) processed.name = name;
        }

        // Map mobile_phone to phone if needed
        if (!processed.phone && row.mobile_phone) {
          processed.phone = row.mobile_phone;
        }

        return processed;
      });

      // Filter out rows without email - check multiple possible email field names
      const validCustomers = customers.map(c => {
        // Try to find email from various possible field names
        const email = c.email || c.e_mail || c.email_address || c.emailaddress || "";
        if (email) {
          return { ...c, email };
        }
        return null;
      }).filter((c): c is NonNullable<typeof c> => c !== null && !!c.email);

      console.log(`Total rows parsed: ${customers.length}`);
      console.log(`Valid customers with email: ${validCustomers.length}`);
      if (customers.length > 0) {
        console.log("Sample row keys:", Object.keys(customers[0]));
        console.log("Sample row:", customers[0]);
      }

      if (validCustomers.length === 0) {
        const sampleKeys = customers.length > 0 ? Object.keys(customers[0]).join(", ") : "none";
        toast.error(`No customers with valid email addresses found. Available columns: ${sampleKeys.substring(0, 100)}...`);
        setIsImporting(false);
        return;
      }

      console.log(`Importing ${validCustomers.length} customers...`, validCustomers[0]);

      // Import in parallel batches - small batch size for CF Worker CPU limits
      const BATCH_SIZE = 15;
      const PARALLEL = 3;
      const totals = { imported: 0, updated: 0, skipped: 0 };
      setImportProgress({ current: 0, total: validCustomers.length, imported: 0, updated: 0, skipped: 0 });

      const allBatches: typeof validCustomers[] = [];
      for (let i = 0; i < validCustomers.length; i += BATCH_SIZE) {
        allBatches.push(validCustomers.slice(i, i + BATCH_SIZE));
      }

      // Process in waves of PARALLEL concurrent requests
      for (let w = 0; w < allBatches.length; w += PARALLEL) {
        const wave = allBatches.slice(w, w + PARALLEL);
        const results = await Promise.allSettled(
          wave.map(batch =>
            fetch("/api/admin/customers/import", {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ customers: batch, usageType: importUsageType }),
            }).then(r => r.json() as Promise<{ data?: { imported: number; updated: number; skipped: number }; error?: string }>)
          )
        );

        let failed = false;
        for (const r of results) {
          if (r.status === "fulfilled" && r.value.data) {
            totals.imported += r.value.data.imported;
            totals.updated += r.value.data.updated;
            totals.skipped += r.value.data.skipped;
          } else {
            failed = true;
          }
        }
        const processed = Math.min((w + wave.length) * BATCH_SIZE, validCustomers.length);
        setImportProgress({ current: processed, total: validCustomers.length, ...totals });
        if (failed) {
          toast.error("Some batches failed - continuing with remaining");
          break;
        }
      }

      toast.success(
        `Imported ${totals.imported} new, updated ${totals.updated}, skipped ${totals.skipped}`
      );
      setIsImportDialogOpen(false);
      setImportData("");
      setImportProgress({ current: 0, total: 0, imported: 0, updated: 0, skipped: 0 });
      fetchCustomers();
    } catch (error) {
      toast.error("Failed to parse or import CSV");
    } finally {
      setIsImporting(false);
    }
  };

  const handleTrailLoansImport = async () => {
    setIsImporting(true);

    try {
      // Parse headerless CSV - columns: email, name, phone, birthday, settlement_date, loan_category, preferred_name, loan_purpose, loan_amount, lender
      const TRAIL_LOAN_COLUMNS = ["email", "name", "phone", "birthday", "settlement_date", "loan_category", "preferred_name", "loan_purpose", "loan_amount", "lender"];

      const lines = importData.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter(l => l.trim());
      const rows: Record<string, string>[] = [];

      for (const line of lines) {
        // Parse CSV line respecting quoted fields
        const values: string[] = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            inQuotes = !inQuotes;
          } else if (ch === "," && !inQuotes) {
            values.push(current.trim());
            current = "";
          } else {
            current += ch;
          }
        }
        values.push(current.trim());

        // Remove BOM from first value
        if (values[0]) values[0] = values[0].replace(/^\ufeff/, "");

        const row: Record<string, string> = {};
        TRAIL_LOAN_COLUMNS.forEach((col, idx) => {
          if (idx < values.length && values[idx]) {
            // Parse date fields
            if (col === "birthday" || col === "settlement_date") {
              row[col] = parseDate(values[idx]);
            } else {
              row[col] = values[idx];
            }
          }
        });

        if (row.email) rows.push(row);
      }

      if (rows.length === 0) {
        toast.error("No valid data found in CSV");
        setIsImporting(false);
        return;
      }

      // Import in parallel batches - small batch size for CF Worker CPU limits
      const BATCH_SIZE = 15;
      const PARALLEL = 3;
      const totals = { imported: 0, updated: 0, skipped: 0, loansCreated: 0 };
      setImportProgress({ current: 0, total: rows.length, imported: 0, updated: 0, skipped: 0 });

      const allBatches: typeof rows[] = [];
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        allBatches.push(rows.slice(i, i + BATCH_SIZE));
      }

      for (let w = 0; w < allBatches.length; w += PARALLEL) {
        const wave = allBatches.slice(w, w + PARALLEL);
        const results = await Promise.allSettled(
          wave.map(batch =>
            fetch("/api/admin/customers/import-trail-loans", {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ rows: batch, usageType: importUsageType }),
            }).then(r => r.json() as Promise<{ data?: { imported: number; updated: number; skipped: number; loansCreated: number }; error?: string }>)
          )
        );

        let failed = false;
        for (const r of results) {
          if (r.status === "fulfilled" && r.value.data) {
            totals.imported += r.value.data.imported;
            totals.updated += r.value.data.updated;
            totals.skipped += r.value.data.skipped;
            totals.loansCreated += r.value.data.loansCreated;
          } else {
            failed = true;
          }
        }
        const processed = Math.min((w + wave.length) * BATCH_SIZE, rows.length);
        setImportProgress({ current: processed, total: rows.length, imported: totals.imported, updated: totals.updated, skipped: totals.skipped });
        if (failed) {
          toast.error("Some batches failed - continuing with remaining");
          break;
        }
      }

      toast.success(
        `Imported ${totals.imported} new customers, updated ${totals.updated}, ${totals.loansCreated} loans created`
      );
      setIsImportDialogOpen(false);
      setImportData("");
      setImportProgress({ current: 0, total: 0, imported: 0, updated: 0, skipped: 0 });
      fetchCustomers();
    } catch (err) {
      toast.error("Failed to parse or import Trail Loans CSV");
    } finally {
      setIsImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = `email,name,phone,birthday,loan_settlement_date,loan_type,preferred_name,notes
john@example.com,John Smith,0400000000,1985-06-15,2022-03-20,home_purchase,Johnny,VIP customer
jane@example.com,Jane Doe,0411111111,1990-12-01,2023-06-10,refinance,,First home buyer`;

    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter customers by search query
  const filteredCustomers = customers.filter(
    (c) => {
      const matchesSearch = c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesUsageType = usageTypeFilter === "all"
        || (usageTypeFilter === "unassigned" && !c.accountStatus)
        || c.accountStatus === usageTypeFilter;
      return matchesSearch && matchesUsageType;
    }
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / CUSTOMERS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedCustomers = filteredCustomers.slice(
    (safePage - 1) * CUSTOMERS_PER_PAGE,
    safePage * CUSTOMERS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, usageTypeFilter]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCustomers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCustomers.map((c) => c.id)));
    }
  };


  const handleBulkAssign = async (usageType: string) => {
    if (selectedIds.size === 0) return;
    setIsBulkUpdating(true);
    const allIds = Array.from(selectedIds);
    const BATCH_SIZE = 200;
    setBulkUpdateProgress({ current: 0, total: allIds.length });

    try {
      // Create all batches
      const batches: string[][] = [];
      for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
        batches.push(allIds.slice(i, i + BATCH_SIZE));
      }

      // Fire all batches in parallel
      const results = await Promise.allSettled(
        batches.map(batch =>
          fetch("/api/admin/customers/bulk-update", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ customerIds: batch, accountStatus: usageType }),
          }).then(r => r.json() as Promise<{ updated?: number }>)
        )
      );

      const totalUpdated = results.reduce((sum, r) =>
        sum + (r.status === "fulfilled" ? (r.value.updated || 0) : 0), 0
      );
      const failed = results.filter(r => r.status === "rejected").length;

      setBulkUpdateProgress({ current: allIds.length, total: allIds.length });
      if (failed > 0) toast.error(`${failed} batch(es) failed`);
      toast.success(`Updated ${totalUpdated} customers to ${usageType}`);
      setSelectedIds(new Set());
      setBulkUpdateProgress({ current: 0, total: 0 });
      fetchCustomers();
    } catch {
      toast.error("Failed to update customers");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setIsBulkDeleting(true);
    const allIds = Array.from(selectedIds);
    const BATCH_SIZE = 200;
    setBulkDeleteProgress({ current: 0, total: allIds.length, deleted: 0 });

    try {
      // Create all batches
      const batches: string[][] = [];
      for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
        batches.push(allIds.slice(i, i + BATCH_SIZE));
      }

      // Fire all batches in parallel
      const results = await Promise.allSettled(
        batches.map(batch =>
          fetch("/api/admin/customers/bulk-delete", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ customerIds: batch }),
          }).then(r => r.json() as Promise<{ deleted?: number }>)
        )
      );

      const totalDeleted = results.reduce((sum, r) =>
        sum + (r.status === "fulfilled" ? (r.value.deleted || 0) : 0), 0
      );
      const failed = results.filter(r => r.status === "rejected").length;

      setBulkDeleteProgress({ current: allIds.length, total: allIds.length, deleted: totalDeleted });
      if (failed > 0) toast.error(`${failed} batch(es) failed`);
      toast.success(`Deleted ${totalDeleted} customers`);
      setSelectedIds(new Set());
      setIsBulkDeleteDialogOpen(false);
      setBulkDeleteProgress({ current: 0, total: 0, deleted: 0 });
      fetchCustomers();
    } catch {
      toast.error("Failed to delete customers");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Stats
  const stats = {
    total: customers.length,
    unassigned: customers.filter((c) => !c.accountStatus).length,
    existing: customers.filter((c) => c.accountStatus === "existing").length,
    ineligible: customers.filter((c) => c.accountStatus === "ineligible").length,
    discharged: customers.filter((c) => c.accountStatus === "discharged").length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-medium text-slate-900">
            Customers
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage customer accounts and automation data
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
            <FiUpload className="mr-2 h-4 w-4" />
            Import
          </Button>
          <Button onClick={() => setIsDialogOpen(true)}>
            <FiPlus className="mr-2 h-4 w-4" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiUsers className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {stats.unassigned > 0 && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FiUsers className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.unassigned}</p>
                  <p className="text-xs text-muted-foreground">Unassigned</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.existing}</p>
                <p className="text-xs text-muted-foreground">Existing (Paying)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FiX className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.ineligible}</p>
                <p className="text-xs text-muted-foreground">Ineligible</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <FiUsers className="w-5 h-5 text-slate-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.discharged}</p>
                <p className="text-xs text-muted-foreground">Discharged</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search customers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <FiX className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Usage Type Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: "all", label: "All" },
          { value: "unassigned", label: "Unassigned" },
          { value: "existing", label: "Existing (Paying)" },
          { value: "ineligible", label: "Ineligible" },
          { value: "discharged", label: "Discharged" },
        ].map((opt) => (
          <Button
            key={opt.value}
            variant={usageTypeFilter === opt.value ? "default" : "outline"}
            size="sm"
            onClick={() => setUsageTypeFilter(opt.value)}
          >
            {opt.label}
            {opt.value !== "all" && (
              <span className="ml-1.5 text-xs opacity-70">
                ({opt.value === "unassigned"
                  ? customers.filter(c => !c.accountStatus).length
                  : customers.filter(c => c.accountStatus === opt.value).length})
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="space-y-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          {/* Select all banner when only current page is selected */}
          {selectedIds.size < filteredCustomers.length && selectedIds.size === paginatedCustomers.length && (
            <button
              onClick={() => setSelectedIds(new Set(filteredCustomers.map(c => c.id)))}
              className="w-full text-center text-sm text-accent hover:underline py-1"
            >
              Select all {filteredCustomers.length} customers (not just this page)
            </button>
          )}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <span className="text-sm font-medium text-primary">
              {selectedIds.size} selected{selectedIds.size === filteredCustomers.length ? " (all)" : ""}
            </span>
            <div className="flex gap-2 flex-wrap flex-1">
              <Button size="sm" variant="outline" onClick={() => handleBulkAssign("existing")} disabled={isBulkUpdating || isBulkDeleting} className="!text-green-700 !border-green-300 hover:!bg-green-50 hover:!text-green-800">
                Set Existing
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAssign("ineligible")} disabled={isBulkUpdating || isBulkDeleting} className="!text-amber-700 !border-amber-300 hover:!bg-amber-50 hover:!text-amber-800">
                Set Ineligible
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAssign("discharged")} disabled={isBulkUpdating || isBulkDeleting} className="!text-slate-600 !border-slate-300 hover:!bg-slate-50 hover:!text-slate-700">
                Set Discharged
              </Button>
            </div>
            <Button size="sm" variant="outline" onClick={() => setIsBulkDeleteDialogOpen(true)} disabled={isBulkUpdating || isBulkDeleting} className="!text-red-700 !border-red-300 hover:!bg-red-50 hover:!text-red-800">
              <FiTrash2 className="w-3.5 h-3.5 mr-1" />
              Delete Selected
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} disabled={isBulkUpdating || isBulkDeleting} className="text-slate-500">
              Clear
          </Button>
          </div>
          {isBulkUpdating && bulkUpdateProgress.total > 0 && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Updating {bulkUpdateProgress.current} of {bulkUpdateProgress.total}...</span>
                <span>{Math.round((bulkUpdateProgress.current / bulkUpdateProgress.total) * 100)}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(bulkUpdateProgress.current / bulkUpdateProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Customers List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={filteredCustomers.length > 0 && selectedIds.size === filteredCustomers.length}
              onChange={toggleSelectAll}
              className="h-4 w-4 rounded"
              title={`Select all ${filteredCustomers.length} customers`}
            />
            <FiUsers className="w-5 h-5" />
            All Customers ({filteredCustomers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-8">
              <FiUsers className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                {searchQuery ? "No customers found" : "No customers yet"}
              </p>
              {!searchQuery && (
                <div className="flex gap-2 justify-center mt-4">
                  <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
                    <FiUpload className="mr-2 h-4 w-4" />
                    Import Customers
                  </Button>
                  <Button onClick={() => setIsDialogOpen(true)}>
                    <FiPlus className="mr-2 h-4 w-4" />
                    Add First Customer
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {paginatedCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className={`flex flex-col lg:flex-row lg:items-center gap-4 p-4 rounded-lg transition-colors ${
                    customer.isLocked
                      ? "bg-red-50 hover:bg-red-100"
                      : "bg-muted/50 hover:bg-muted"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(customer.id)}
                    onChange={() => toggleSelect(customer.id)}
                    className="h-4 w-4 rounded shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/admin/customers/${customer.id}`} className="font-medium hover:text-accent underline-offset-2 hover:underline transition-colors">
                        {customer.name || "No name"}
                      </Link>
                      {customer.preferredName && (
                        <span className="text-sm text-muted-foreground">
                          ({customer.preferredName})
                        </span>
                      )}
                      {customer.isLocked && (
                        <Badge variant="secondary" className="bg-red-100 text-red-700 text-xs">
                          Locked
                        </Badge>
                      )}
                      {customer.emailVerified && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                          <FiCheck className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <FiMail className="w-3 h-3" />
                        {customer.email}
                      </span>
                      {customer.phone && (
                        <span className="flex items-center gap-1">
                          <FiPhone className="w-3 h-3" />
                          {customer.phone}
                        </span>
                      )}
                      {customer.birthday && (
                        <span className="flex items-center gap-1">
                          <FiGift className="w-3 h-3" />
                          {customer.birthday}
                        </span>
                      )}
                      {customer.accountStatus && ["existing", "ineligible", "discharged"].includes(customer.accountStatus) ? (
                        <Badge variant="secondary" className={`text-xs ${
                          customer.accountStatus === "existing" ? "bg-green-100 text-green-700" :
                          customer.accountStatus === "ineligible" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>
                          {customer.accountStatus === "existing" ? "Existing" :
                           customer.accountStatus === "ineligible" ? "Ineligible" : "Discharged"}
                        </Badge>
                      ) : !customer.accountStatus && (
                        <Badge variant="outline" className="text-xs text-purple-600 border-purple-200 border-dashed">
                          Unassigned
                        </Badge>
                      )}
                      {customer.loanType && (
                        <Badge variant="secondary" className="text-xs">
                          {loanTypeLabels[customer.loanType] || customer.loanType}
                        </Badge>
                      )}
                      {customer.loanCount > 0 && (
                        <Badge variant="secondary" className={`text-xs ${customer.loanCount > 1 ? "bg-blue-100 text-blue-700" : ""}`}>
                          {customer.loanCount} {customer.loanCount === 1 ? "loan" : "loans"}
                        </Badge>
                      )}
                    </div>
                    {customer.loanSettlementDate && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Settlement: {new Date(customer.loanSettlementDate).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(customer)}
                      title="Edit"
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={customer.isLocked ? "outline" : "ghost"}
                      size="sm"
                      onClick={() => handleToggleLock(customer.id, customer.isLocked)}
                      disabled={lockingId === customer.id}
                      className={customer.isLocked ? "text-green-700" : "text-red-600"}
                      title={customer.isLocked ? "Unlock" : "Lock"}
                    >
                      {lockingId === customer.id ? (
                        <span className="animate-spin">...</span>
                      ) : customer.isLocked ? (
                        <FiUnlock className="w-4 h-4" />
                      ) : (
                        <FiLock className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteDialog(customer)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {filteredCustomers.length > CUSTOMERS_PER_PAGE && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 sm:px-6 py-3 border-t">
            <p className="text-xs sm:text-sm text-muted-foreground">
              {((safePage - 1) * CUSTOMERS_PER_PAGE) + 1}–{Math.min(safePage * CUSTOMERS_PER_PAGE, filteredCustomers.length)} of {filteredCustomers.length}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={safePage <= 1}
                className="h-8 w-8 p-0"
              >
                <FiChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="h-8 w-8 p-0"
              >
                <FiChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium px-3">
                Page {safePage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="h-8 w-8 p-0"
              >
                <FiChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={safePage >= totalPages}
                className="h-8 w-8 p-0"
              >
                <FiChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create Customer Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Email *</label>
                <Input
                  type="email"
                  placeholder="customer@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Full Name *</label>
                <Input
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Preferred Name</label>
                <Input
                  placeholder="Johnny"
                  value={formData.preferredName}
                  onChange={(e) => setFormData({ ...formData, preferredName: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input
                  type="tel"
                  placeholder="0400 000 000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Birthday</label>
                <Input
                  type="date"
                  value={formData.birthday}
                  onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Loan Type</label>
                <Select
                  value={formData.loanType}
                  onValueChange={(value) => setFormData({ ...formData, loanType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home_purchase">Home Purchase</SelectItem>
                    <SelectItem value="refinance">Refinance</SelectItem>
                    <SelectItem value="investment">Investment</SelectItem>
                    <SelectItem value="first_home">First Home</SelectItem>
                    <SelectItem value="construction">Construction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Settlement Date</label>
                <Input
                  type="date"
                  value={formData.loanSettlementDate}
                  onChange={(e) => setFormData({ ...formData, loanSettlementDate: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Customer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog - Full Screen */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="!w-[98vw] sm:!w-[95vw] !max-w-[1400px] !h-[95vh] sm:!h-[90vh] !max-h-[95vh] sm:!max-h-[90vh] !p-0 !gap-0 flex flex-col" showCloseButton={false}>
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b bg-white">
            <div>
              <DialogTitle className="text-xl">Edit Customer</DialogTitle>
              <DialogDescription className="mt-1">
                {selectedCustomer?.email}
                {selectedCustomer?.crmId && <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded">{selectedCustomer.crmId}</span>}
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditDialogOpen(false)}
              className="h-8 w-8 p-0"
            >
              <FiX className="w-5 h-5" />
            </Button>
          </div>

          {/* Scrollable Content */}
          <form onSubmit={handleEditSubmit} className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-6 space-y-6">

              {/* Account Status & Customer Type — most important, top of form */}
              <div className="border rounded-lg p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Classification</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Account Status</label>
                    <Select value={editFormData.accountStatus || "unassigned"} onValueChange={(value) => setEditFormData({ ...editFormData, accountStatus: value === "unassigned" ? "" : value })}>
                      <SelectTrigger><SelectValue placeholder="Select status..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        <SelectItem value="existing">Existing (Paying)</SelectItem>
                        <SelectItem value="ineligible">Ineligible</SelectItem>
                        <SelectItem value="discharged">Discharged</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Customer Type</label>
                    <Select value={editFormData.customerType} onValueChange={(value) => setEditFormData({ ...editFormData, customerType: value })}>
                      <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="joint">Joint</SelectItem>
                        <SelectItem value="company">Company</SelectItem>
                        <SelectItem value="trust">Trust</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="border rounded-lg p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Personal Information</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Title</label>
                    <Select value={editFormData.title} onValueChange={(value) => setEditFormData({ ...editFormData, title: value })}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mr">Mr</SelectItem>
                        <SelectItem value="Mrs">Mrs</SelectItem>
                        <SelectItem value="Ms">Ms</SelectItem>
                        <SelectItem value="Miss">Miss</SelectItem>
                        <SelectItem value="Dr">Dr</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">First Name</label>
                    <Input value={editFormData.firstName} onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Last Name</label>
                    <Input value={editFormData.lastName} onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Preferred Name</label>
                    <Input value={editFormData.preferredName} onChange={(e) => setEditFormData({ ...editFormData, preferredName: e.target.value })} placeholder="What they like to be called" className="placeholder:italic placeholder:text-slate-300" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Gender</label>
                    <Select value={editFormData.gender} onValueChange={(value) => setEditFormData({ ...editFormData, gender: value })}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Male</SelectItem>
                        <SelectItem value="F">Female</SelectItem>
                        <SelectItem value="O">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date of Birth</label>
                    <Input type="date" value={editFormData.birthday} onChange={(e) => setEditFormData({ ...editFormData, birthday: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Spouse / Partner</label>
                    <Input value={editFormData.spousePartner} onChange={(e) => setEditFormData({ ...editFormData, spousePartner: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Driver's Licence</label>
                    <Input value={editFormData.driversLicence} onChange={(e) => setEditFormData({ ...editFormData, driversLicence: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="border rounded-lg p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Contact Details</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Mobile Phone</label>
                    <Input value={editFormData.phone} onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Home Phone</label>
                    <Input value={editFormData.homePhone} onChange={(e) => setEditFormData({ ...editFormData, homePhone: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Work Phone</label>
                    <Input value={editFormData.workPhone} onChange={(e) => setEditFormData({ ...editFormData, workPhone: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Best Time to Call</label>
                    <Input value={editFormData.bestTimeToCall} onChange={(e) => setEditFormData({ ...editFormData, bestTimeToCall: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Contact Method</label>
                    <Select value={editFormData.contactMethod} onValueChange={(value) => setEditFormData({ ...editFormData, contactMethod: value })}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="border rounded-lg p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Address</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Street</label>
                    <Input value={editFormData.street} onChange={(e) => setEditFormData({ ...editFormData, street: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">City / Suburb</label>
                    <Input value={editFormData.city} onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">State</label>
                    <Select value={editFormData.state} onValueChange={(value) => setEditFormData({ ...editFormData, state: value })}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="QLD">QLD</SelectItem>
                        <SelectItem value="NSW">NSW</SelectItem>
                        <SelectItem value="VIC">VIC</SelectItem>
                        <SelectItem value="WA">WA</SelectItem>
                        <SelectItem value="SA">SA</SelectItem>
                        <SelectItem value="TAS">TAS</SelectItem>
                        <SelectItem value="ACT">ACT</SelectItem>
                        <SelectItem value="NT">NT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Post Code</label>
                    <Input value={editFormData.postCode} onChange={(e) => setEditFormData({ ...editFormData, postCode: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Country</label>
                    <Input value={editFormData.country} onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })} placeholder="Australia" className="placeholder:italic placeholder:text-slate-300" />
                  </div>
                </div>
              </div>

              {/* Loan & Employment side by side */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Loan */}
                <div className="border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4">Loan Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Loan Type</label>
                      <Select value={editFormData.loanType} onValueChange={(value) => setEditFormData({ ...editFormData, loanType: value })}>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="home_purchase">Home Purchase</SelectItem>
                          <SelectItem value="refinance">Refinance</SelectItem>
                          <SelectItem value="investment">Investment</SelectItem>
                          <SelectItem value="first_home">First Home</SelectItem>
                          <SelectItem value="construction">Construction</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Settlement Date</label>
                      <Input type="date" value={editFormData.loanSettlementDate} onChange={(e) => setEditFormData({ ...editFormData, loanSettlementDate: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Product Group</label>
                      <Input value={editFormData.productGroup} onChange={(e) => setEditFormData({ ...editFormData, productGroup: e.target.value })} />
                    </div>
                  </div>

                  {/* Customer Loans from customer_loans table */}
                  {customerLoans.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">All Loans ({customerLoans.length})</h4>
                      <div className="space-y-2">
                        {customerLoans.map((loan) => (
                          <div key={loan.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                            <div>
                              <span className="font-medium text-slate-800">{loan.loanPurpose || loan.loanType || "Loan"}</span>
                              {loan.lender && <span className="text-slate-500 ml-2">with {loan.lender}</span>}
                            </div>
                            <div className="text-right">
                              {loan.loanAmount && <span className="font-medium text-slate-700">${Number(loan.loanAmount).toLocaleString()}</span>}
                              {loan.settlementDate && (
                                <span className="text-slate-500 ml-2">{new Date(loan.settlementDate).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Employment */}
                <div className="border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4">Employment</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Occupation</label>
                      <Input value={editFormData.occupation} onChange={(e) => setEditFormData({ ...editFormData, occupation: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Job Title</label>
                      <Input value={editFormData.jobTitle} onChange={(e) => setEditFormData({ ...editFormData, jobTitle: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Company</label>
                      <Input value={editFormData.company} onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Industry</label>
                      <Input value={editFormData.industry} onChange={(e) => setEditFormData({ ...editFormData, industry: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">ABN</label>
                      <Input value={editFormData.abn} onChange={(e) => setEditFormData({ ...editFormData, abn: e.target.value })} />
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={editFormData.selfEmployed} onChange={(e) => setEditFormData({ ...editFormData, selfEmployed: e.target.checked })} className="h-4 w-4 rounded" />
                        <span className="text-sm font-medium text-slate-700">Self Employed</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Marketing */}
              <div className="border rounded-lg p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Marketing Preferences</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Source</label>
                    <Input value={editFormData.source} onChange={(e) => setEditFormData({ ...editFormData, source: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Campaign</label>
                    <Input value={editFormData.campaign} onChange={(e) => setEditFormData({ ...editFormData, campaign: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Channel</label>
                    <Input value={editFormData.channel} onChange={(e) => setEditFormData({ ...editFormData, channel: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-6 mt-4 pt-4 border-t">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editFormData.neverEmail} onChange={(e) => setEditFormData({ ...editFormData, neverEmail: e.target.checked })} className="h-4 w-4 rounded" />
                    <span className="text-sm font-medium text-slate-700">Never Email</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editFormData.dontSendMarketing} onChange={(e) => setEditFormData({ ...editFormData, dontSendMarketing: e.target.checked })} className="h-4 w-4 rounded" />
                    <span className="text-sm font-medium text-slate-700">No Marketing</span>
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div className="border rounded-lg p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Notes</h3>
                <Textarea
                  placeholder="Any notes about this customer..."
                  value={editFormData.customerNotes}
                  onChange={(e) => setEditFormData({ ...editFormData, customerNotes: e.target.value })}
                  rows={4}
                  className="resize-none placeholder:italic placeholder:text-slate-300"
                />
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t bg-slate-50">
            <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-full p-0 gap-0 overflow-hidden">
          <div className="bg-primary px-6 pt-5 pb-4 space-y-3">
            <DialogHeader className="gap-0">
              <DialogTitle className="font-serif text-lg text-primary-foreground">Import Customers</DialogTitle>
              <DialogDescription className="text-primary-foreground/60 text-xs">
                Upload a CSV file to import customers in bulk
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                <span className="text-[11px] font-medium text-primary-foreground/50 uppercase tracking-wider">Format</span>
                <div className="flex gap-1 bg-white/10 rounded-full p-0.5 w-fit">
                  {[
                    { value: "standard" as const, label: "Standard CSV", icon: HiOutlineTableCells },
                    { value: "trail_loans" as const, label: "Headerless", icon: HiOutlineDocumentText },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setImportFormat(opt.value)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        importFormat === opt.value
                          ? "bg-white text-primary shadow-sm"
                          : "text-primary-foreground/70 hover:text-primary-foreground"
                      }`}
                    >
                      <opt.icon className="w-3.5 h-3.5" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                <span className="text-[11px] font-medium text-primary-foreground/50 uppercase tracking-wider">Assign Type</span>
                <div className="flex gap-1 bg-white/10 rounded-full p-0.5 flex-wrap w-fit">
                  {[
                    { value: "", label: "Unassigned", dot: "bg-purple-400" },
                    { value: "existing", label: "Existing", dot: "bg-green-400" },
                    { value: "ineligible", label: "Ineligible", dot: "bg-amber-400" },
                    { value: "discharged", label: "Discharged", dot: "bg-slate-400" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setImportUsageType(opt.value)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        importUsageType === opt.value
                          ? "bg-white text-primary shadow-sm"
                          : "text-primary-foreground/70 hover:text-primary-foreground"
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${importUsageType === opt.value ? opt.dot : "bg-current opacity-40"}`} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-5">

            {/* Drop Zone - compact */}
            <div
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`
                relative border-2 border-dashed rounded-2xl px-6 py-5 text-center cursor-pointer
                transition-all duration-200
                ${isDragging
                  ? "border-accent bg-accent/5 scale-[1.01]"
                  : importData
                    ? "border-green-300 bg-green-50/50"
                    : "border-slate-200 hover:border-accent/40 hover:bg-slate-50"
                }
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.txt"
                className="hidden"
                onChange={handleFileUpload}
              />
              {importData ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center">
                      <FiCheckCircle className="w-4.5 h-4.5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-800">
                        {importFormat === "trail_loans" || isHeaderlessCSV(importData)
                          ? `${importData.replace(/\r\n/g, "\n").split("\n").filter(l => l.trim()).length} rows`
                          : `${parseCSV(importData).length} rows`
                        }
                        {" "}ready to import
                      </p>
                      <p className="text-xs text-slate-500">
                        {importFormat === "trail_loans" || isHeaderlessCSV(importData) ? "Headerless format detected" : "Standard CSV with headers"}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setImportData(""); }}
                    className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
                  >
                    <FiX className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                    isDragging ? "bg-accent/10" : "bg-slate-100"
                  }`}>
                    <FiUpload className={`w-5 h-5 ${isDragging ? "text-accent" : "text-slate-400"}`} />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-sm font-medium text-slate-700">
                      {isDragging ? "Drop file here" : "Drop a CSV file or click to browse"}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Supports .csv, .tsv, .txt
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="pointer-events-none text-xs h-8">
                    Browse
                  </Button>
                </div>
              )}
            </div>

            {/* Expandable paste area - only show when no file */}
            {!importData && (
              <details className="group">
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 flex items-center gap-1">
                  <FiChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
                  Or paste CSV data directly
                </summary>
                <Textarea
                  placeholder="Paste CSV data here..."
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  rows={4}
                  className="font-mono text-xs mt-2"
                />
              </details>
            )}

            {/* Progress */}
            {isImporting && importProgress.total > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Importing {importProgress.current} of {importProgress.total}...</span>
                  <span>{Math.round((importProgress.current / importProgress.total) * 100)}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-accent h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                  />
                </div>
                <div className="flex gap-3 text-xs">
                  <span className="text-green-600">{importProgress.imported} new</span>
                  <span className="text-blue-600">{importProgress.updated} updated</span>
                  {importProgress.skipped > 0 && <span className="text-amber-600">{importProgress.skipped} skipped</span>}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-3 bg-slate-50 border-t">
            <Button variant="ghost" size="sm" onClick={downloadTemplate} className="text-xs text-slate-500 h-8">
              <FiDownload className="w-3.5 h-3.5 mr-1.5" />
              Template
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setIsImportDialogOpen(false); setImportUsageType("existing"); setImportFormat("standard"); }} disabled={isImporting} className="h-8">
                Cancel
              </Button>
              <Button size="sm" onClick={handleImport} disabled={isImporting || !importData.trim()} className="h-8 px-5">
                {isImporting ? "Importing..." : "Import"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this customer? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {customerToDelete && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="font-medium text-slate-900">{customerToDelete.name || "No name"}</p>
              <p className="text-sm text-slate-600">{customerToDelete.email}</p>
              {customerToDelete.phone && (
                <p className="text-sm text-slate-500">{customerToDelete.phone}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setCustomerToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete {selectedIds.size} Customers</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedIds.size}</strong> selected customers? This action cannot be undone and will permanently remove all their data.
            </DialogDescription>
          </DialogHeader>
          {!isBulkDeleting && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm font-medium text-red-800">
                This will permanently delete {selectedIds.size} customer{selectedIds.size !== 1 ? "s" : ""} and also remove them from the subscribers list.
              </p>
            </div>
          )}
          {isBulkDeleting && bulkDeleteProgress.total > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Deleting {bulkDeleteProgress.current} of {bulkDeleteProgress.total}...</span>
                <span>{Math.round((bulkDeleteProgress.current / bulkDeleteProgress.total) * 100)}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-red-500 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">{bulkDeleteProgress.deleted} deleted so far</p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsBulkDeleteDialogOpen(false)}
              disabled={isBulkDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
            >
              {isBulkDeleting ? "Deleting..." : `Delete ${selectedIds.size} Customers`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
